import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import uniq from "lodash/uniq";

import {
  multiSelectChipAndFocusCss,
  buildMultiSelectDropdownGlobalStyle
} from "@tailormed/common-client/component/Select/MultiSelectFieldStyles";

const { Option } = Select;

const FACILITIES_DROPDOWN_CLASS = "tm-facilities-dropdown";

const StyledSelect = styled(Select)`
  ${multiSelectChipAndFocusCss}
`;

const FacilitiesDropdownGlobalStyle = buildMultiSelectDropdownGlobalStyle(FACILITIES_DROPDOWN_CLASS);

const FacilitiesSelectField = ({
  facilities,
  value,
  onChange,
  loading,
  disabled,
  placeholder,
  bordered = true,
  showArrow,
  allowClear = false,
  maxTagCount = 1,
  maxTagTextLength,
  maxTagPlaceholder,
  dropdownMatchSelectWidth = false,
  dropdownStyle,
  style,
  autoFocus,
  defaultOpen,
  open,
  onBlur,
  onKeyDown,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onDropdownVisibleChange,
  getPopupContainer = (trigger) => trigger.parentNode,
  id,
  nonRemovableFacilityIds = []
}) => {
  const valueArray = Array.isArray(value) ? value : [];
  const nonRemovableFacilityIdsSet = useMemo(
    () => new Set((nonRemovableFacilityIds || []).map(Number)),
    [nonRemovableFacilityIds]
  );
  const facilityNameById = useMemo(
    () => new Map((facilities || []).map((facility) => [Number(facility.id), facility.name])),
    [facilities]
  );

  const compareFacilities = (a, b) => {
    const aSelected = valueArray.indexOf(a.value) > -1;
    const bSelected = valueArray.indexOf(b.value) > -1;
    if (aSelected === bSelected) {
      return (a.children || "").localeCompare(b.children || "");
    }
    return aSelected ? -1 : 1;
  };

  const filterOption = (input, option) => option.children?.toLowerCase()?.includes(input?.toLowerCase());

  const handleChange = (next) => {
    const nextArray = Array.isArray(next) ? next : [];
    // Re-include any non-removable facility the user tried to drop, so it can't be removed via the
    // tag "x" or a dropdown deselect - both paths call this same handler.
    const stickyNonRemovableIds = valueArray.filter((facilityId) => nonRemovableFacilityIdsSet.has(Number(facilityId)));
    if (typeof onChange === "function") onChange(uniq([...nextArray, ...stickyNonRemovableIds]));
  };

  const tagRender = useCallback(
    ({ label, value: tagValue, disabled: itemDisabled, closable, onClose }) => {
      const isRemovable = closable && !nonRemovableFacilityIdsSet.has(Number(tagValue));
      // antd truncates the label handed to tagRender when maxTagTextLength is set, so look up the
      // untruncated name for the tooltip title.
      const fullName = facilityNameById.get(Number(tagValue));
      return (
        <span
          className={`ant-select-selection-item${itemDisabled ? " ant-select-selection-item-disabled" : ""}`}
          title={fullName ?? (typeof label === "string" ? label : undefined)}
        >
          <span className="ant-select-selection-item-content">{label}</span>
          {isRemovable && (
            // Hand-rolled to match antd's own remove-tag markup, since multiSelectChipAndFocusCss
            // targets these exact classNames/structure - check that styling on any antd upgrade.
            <span
              className="ant-select-selection-item-remove"
              aria-hidden="true"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onClose}
            >
              <CloseOutlined />
            </span>
          )}
        </span>
      );
    },
    [nonRemovableFacilityIdsSet, facilityNameById]
  );

  return (
    <div id={id} style={{ width: "100%" }}>
      <FacilitiesDropdownGlobalStyle />
      <StyledSelect
        mode="multiple"
        value={valueArray}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDropdownVisibleChange={onDropdownVisibleChange}
        loading={loading}
        disabled={disabled}
        placeholder={placeholder}
        bordered={bordered}
        showArrow={showArrow}
        allowClear={allowClear}
        maxTagCount={maxTagCount}
        maxTagTextLength={maxTagTextLength}
        maxTagPlaceholder={maxTagPlaceholder}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        dropdownClassName={FACILITIES_DROPDOWN_CLASS}
        dropdownStyle={dropdownStyle}
        virtual={false}
        style={style}
        autoFocus={autoFocus}
        defaultOpen={defaultOpen}
        open={open}
        filterOption={filterOption}
        filterSort={compareFacilities}
        getPopupContainer={getPopupContainer}
        tagRender={tagRender}
      >
        {(facilities || []).map((facility, index) => {
          // A non-removable facility the patient already holds must not be toggled off from the
          // open dropdown either (the hidden tag "x" only covers the chip) - disable that option so
          // no no-op deselect fires a save + "updated" toast.
          const isSticky =
            nonRemovableFacilityIdsSet.has(Number(facility.id)) &&
            valueArray.some((v) => Number(v) === Number(facility.id));
          return (
            <Option key={index} value={facility.id} disabled={loading || isSticky}>
              {facility.name}
            </Option>
          );
        })}
      </StyledSelect>
    </div>
  );
};

FacilitiesSelectField.propTypes = {
  facilities: PropTypes.array.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.node,
  bordered: PropTypes.bool,
  showArrow: PropTypes.bool,
  allowClear: PropTypes.bool,
  maxTagCount: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["responsive"])]),
  maxTagTextLength: PropTypes.number,
  maxTagPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  dropdownMatchSelectWidth: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  dropdownStyle: PropTypes.object,
  style: PropTypes.object,
  autoFocus: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onDropdownVisibleChange: PropTypes.func,
  getPopupContainer: PropTypes.func,
  id: PropTypes.string,
  nonRemovableFacilityIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

export default FacilitiesSelectField;
